import { EventTypes } from '@racemap/sdk/schema/events';
import classnames from 'classnames';
import type React from 'react';
import { sendAnalyticsEvent } from 'src/lib/googleAnalytics';
import { type StarterType, useStore } from 'src/lib/player/store';
import {
  formatDistance,
  formatTimeDurationHuman,
  noop,
  onlyMouseEvents,
} from 'src/lib/player/utils';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';

const SEEN_DEVICE_CLASSES = ['Mobile', 'Tracker', 'TrackBox'];

export function formatStarterLabel(starter: StarterType): string {
  if (starter.startNumber != null && starter.startNumber !== '') {
    if (starter.name != null && starter.name !== '') {
      return `${starter.startNumber}. ${starter.name}`;
    }
    return `${starter.startNumber}.`;
  }
  if (starter.name != null && starter.name !== '') {
    return starter.name;
  }
  return '👻';
}

type Props = {
  starter: StarterType;
  starterOpacity: number;
  starterOffset: number;
  starterTimestamp: number;
  onClick: (starter: StarterType, isSelected: boolean) => void;
};

export default function StarterListItem({
  starter,
  starterOpacity,
  starterOffset,
  starterTimestamp,
  onClick = noop,
}: Props): JSX.Element {
  const toggleHidden = useStore((state) => state.actions.toggleHidden);
  const setHighlighted = useStore((state) => state.actions.setHighlighted);
  const toggleSelectedStarter = useStore((state) => state.actions.toggleSelectedStarter);

  const isLive = useStore((state) => state.timer.isLive);
  const now = useStore((state) => state.timer.time);
  const isSelected = useStore((state) => state.timer.selected.includes(starter.id));
  const isSelectedOrHighlighted = useStore(
    (state) => state.timer.highlighted === starter.id || state.timer.selected.includes(starter.id),
  );
  const isVisible = useStore((state) => !state.timer.hidden.has(starter.id));
  const label = formatStarterLabel(starter);
  const eventNames = useStore((state) =>
    state.currentEventIndices.length > 1 && state.options.type === EventTypes.CONTEST_GROUP
      ? state.startersById
          ?.get(starter.id)
          ?.filter((s) => state.currentEventIndices.includes(s.eventIndex))
          .map((s) => state.events[s.eventIndex].shortName)
          .join(', ')
      : null,
  );
  const unitType = useStore((state) => state.options.unitType);

  function handleClick() {
    if (isSelectedOrHighlighted) {
      sendAnalyticsEvent('starter-list-item-click', 'starter-unselect');
    } else {
      sendAnalyticsEvent('starter-list-item-click', 'starter-select');
    }
    toggleSelectedStarter(starter.id);
    onClick(starter, !isSelected);
  }

  function handleHideClick(
    ev:
      | React.MouseEvent<HTMLElement>
      | React.TouchEvent<HTMLElement>
      | React.KeyboardEvent<HTMLElement>,
  ) {
    ev.stopPropagation();
    toggleHidden(starter.id);
  }

  return (
    <div
      className={classnames('starter-list-item', {
        selected: isSelectedOrHighlighted,
      })}
      {...onClickGenerator(handleClick)}
      onPointerEnter={onlyMouseEvents(() => {
        setHighlighted(starter.id);
      })}
      onPointerLeave={onlyMouseEvents(() => {
        setHighlighted(null);
      })}
    >
      <div style={{ opacity: 0.5 * starterOpacity + 0.5 }}>
        <div className="starter-color" style={{ backgroundColor: starter.markerColor.hex }} />
        <div className="starter-text">
          <div className="starter-name" title={label}>
            <div className="starter-name-content">{label}&nbsp;</div>
          </div>
          {(eventNames != null || starterOffset >= 0) && (
            <div className="starter-info">
              {eventNames != null && <span title={eventNames}>{eventNames}</span>}
              {starterOffset >= 0 && <span>{formatDistance(starterOffset, unitType)}</span>}
              {isLive &&
                starterTimestamp > 0 &&
                SEEN_DEVICE_CLASSES.includes(starter.deviceClass) && (
                  <span>{formatTimeDurationHuman(now - starterTimestamp)}</span>
                )}
            </div>
          )}
        </div>
        <div className="starter-controls">
          {isSelected ? <i className="fas fa-star" /> : <i className="far fa-star" />}
          {isVisible ? (
            <i className="far fa-eye" {...onClickGenerator(handleHideClick)} />
          ) : (
            <i className="far fa-eye-slash" {...onClickGenerator(handleHideClick)} />
          )}
        </div>
      </div>
    </div>
  );
}
